import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { Billing, BillingPlan } from '../api.js';
import type { DashboardActions, DashboardState } from '../store.js';
import { ErrorNote, Stat, fmtInt } from '../ui/bits.js';

/**
 * Billing: the plan, the meter, and two buttons that leave for Stripe.
 *
 * A SECTION OF SETTINGS, NOT A SCREEN. It was the third top-level tab; the
 * strip now says Settings, and this renders under that screen's own heading,
 * which is why every heading here is an h2 and nothing here wraps itself in a
 * `.screen`. `#/billing` still opens it.
 *
 * THE HONESTY RULE FROM THE USAGE SCREEN, APPLIED TO MONEY. That screen prints
 * "these are counters, not a bill" because there was no meter behind the price
 * on /pricing. Here the meter exists, and so does a state in which it is not
 * wired to anything: a host with no Stripe key answers `configured:false` (or
 * 503 `billing_disabled`), and the screen renders exactly that — "Billing is
 * not configured on this host. Nothing here can charge anyone." — with the
 * buttons disabled rather than a plan card that looks like an invoice.
 *
 * NO PRICE THE API DID NOT SEND. The plan's name, its included calls and its
 * site count come from `GET /api/billing`, which reads
 * `apps/server/src/billing/plans.ts`; a number restated here would be a second
 * copy that drifts from the one Stripe actually charges. The same goes for the
 * plan list under "Upgrade": drawn from the response when it carries one, and
 * otherwise limited to the two ids the checkout route accepts, with no figure
 * beside them.
 *
 * Both buttons POST first and only then `location.assign` the URL the server
 * returned. The page never composes a Stripe URL itself.
 */

/** The ids `POST /api/billing/checkout` accepts, when the server lists no plans. */
const CHECKOUT_PLANS = ['berth', 'fleet'] as const;

function planLine(p: BillingPlan): string {
  const calls = p.callsIncluded === null ? 'calls by arrangement' : `${fmtInt(p.callsIncluded)} calls / month`;
  const crawls = p.crawls === null ? 'crawls by arrangement' : `${p.crawls} ${p.crawls === 1 ? 'crawl' : 'crawls'}`;
  return `${calls} · ${crawls}`;
}

function Disabled(props: { state: DashboardState; why: string }): VNode {
  return html`
    <div class="setsec" id="billing">
      <h2 class="sec__h">Billing</h2>
      <div class="billoff" role="status">
        <p class="billoff__h">Billing is not configured on this host. Nothing here can charge anyone.</p>
        <p class="billoff__p">${props.why}</p>
      </div>
      <div class="billing__actions">
        <button type="button" class="btn" disabled title="Billing is not configured on this host.">
          Upgrade
        </button>
        <button type="button" class="btn btn--ghost" disabled title="Billing is not configured on this host.">
          Manage subscription
        </button>
      </div>
      <p class="screen__foot">
        Stripe is switched on by setting <code>CLAWHARBOR_STRIPE_SECRET_KEY</code>,${SP}
        <code>CLAWHARBOR_STRIPE_WEBHOOK_SECRET</code> and a price id per paid plan on the server.
        Until then every <code>/api/billing</code> route answers that it is disabled, and the usage
        screen's counters remain what they say they are: counters.
      </p>
    </div>
  `;
}

export function BillingSection(props: { state: DashboardState; actions: DashboardActions }): VNode {
  const { state, actions } = props;

  if (state.error) {
    return html`
      <div class="setsec" id="billing">
        <h2 class="sec__h">Billing</h2>
        ${ErrorNote({ message: state.error, label: 'Could not load billing.' })}
        <p class="sec__p">
          Nothing below is a fallback: there is no cached plan and no assumed allowance, because a
          number printed here is one a person would pay against.
        </p>
        <button type="button" class="btn" onClick=${() => actions.reload()}>Reload</button>
      </div>
    `;
  }

  if (state.billing === null) {
    return html`
      <div class="setsec" id="billing">
        <h2 class="sec__h">Billing</h2>
        <p class="sec__p">${state.loading ? 'Loading the plan…' : 'Nothing loaded yet.'}</p>
      </div>
    `;
  }

  if (state.billing === 'disabled') {
    return Disabled({
      state,
      why: 'The server answered 503 billing_disabled: no Stripe key is set, so there is no plan, no meter and no checkout to start.',
    });
  }

  const b: Billing = state.billing;
  if (!b.configured) {
    return Disabled({
      state,
      why: `The server reports the plan "${b.plan.name}" with ${planLine(b.plan)} and ${fmtInt(b.usage.used)} calls used this period, but it has no Stripe key, so none of that is billed to anybody.`,
    });
  }

  const busy = state.billingBusy;
  const upgrades: BillingPlan[] = b.plans.length
    ? b.plans.filter((p) => p.id !== b.plan.id)
    : CHECKOUT_PLANS.filter((id) => id !== b.plan.id).map((id) => ({
        id,
        name: id,
        callsIncluded: null,
        crawls: null,
        price: null,
      }));
  const sub = b.subscription;

  return html`
    <div class="setsec" id="billing">
      <h2 class="sec__h">Billing: ${b.plan.name}</h2>
      <p class="sec__p">
        Your current plan${b.plan.price ? html`${SP}·${SP}${b.plan.price}` : null}. ${planLine(b.plan)}.
        ${sub
          ? html`${SP}Subscription <b>${sub.status}</b>${sub.currentPeriodEnd
                ? html`, current period ends <span class="when" title=${sub.currentPeriodEnd}>${sub.currentPeriodEnd.slice(0, 10)}</span>`
                : null}.`
          : html`${SP}No subscription is on record for this account.`}
      </p>

      <div class="stats">
        ${Stat({
          label: 'Calls used this period',
          value: fmtInt(b.usage.used),
          note: b.plan.callsIncluded === null ? 'no cap on this plan' : `of ${fmtInt(b.plan.callsIncluded)} included`,
        })}
        ${Stat({
          label: 'Remaining',
          value: b.usage.remaining === null ? '—' : fmtInt(b.usage.remaining),
          note: b.usage.remaining === null ? 'not reported for this plan' : b.usage.remaining === 0 ? 'the included calls are used up' : undefined,
        })}
        ${Stat({
          label: 'Sites',
          value: b.plan.crawls === null ? '—' : String(b.plan.crawls),
          note: b.plan.crawls === null ? 'by arrangement' : 'included in the plan',
        })}
      </div>

      <h3 class="sec__h3">Change plan</h3>
      <p class="sec__p">
        Upgrading opens a Stripe checkout for the plan you pick; managing opens Stripe's own portal,
        where the card, the invoices and cancellation live. Nothing on this page charges anything
        by itself — both buttons hand you to Stripe and come back here when it is done.
      </p>
      ${ErrorNote({ message: state.billingError })}
      <div class="billing__actions">
        ${upgrades.map(
          (p) => html`<button
            type="button"
            class="btn"
            disabled=${busy !== null}
            onClick=${() => actions.checkout(p.id)}
          >
            ${busy === 'checkout' ? 'Opening checkout…' : `Upgrade to ${p.name}`}
            ${p.price ? html`<span class="billing__price">${p.price}</span>` : null}
          </button>`,
        )}
        <button
          type="button"
          class="btn btn--ghost"
          disabled=${busy !== null}
          onClick=${() => actions.openPortal()}
        >
          ${busy === 'portal' ? 'Opening the portal…' : 'Manage subscription'}
        </button>
      </div>
      ${b.plans.length
        ? html`<ul class="plans">
            ${b.plans.map(
              (p) => html`<li class=${p.id === b.plan.id ? 'plan plan--current' : 'plan'}>
                <span class="plan__name">${p.name}</span>
                <span class="plan__line">${planLine(p)}</span>
                ${p.price ? html`<span class="plan__price">${p.price}</span>` : null}
                ${p.id === b.plan.id ? html`<span class="pill pill--good">current</span>` : null}
              </li>`,
            )}
          </ul>`
        : html`<p class="screen__foot">
            The server sent no plan list with this answer, so the upgrade buttons name the plans
            the checkout route accepts and show no prices: a figure that did not come from the API
            has no business next to a Pay button.
          </p>`}

      <p class="screen__foot">
        Usage here is the same meter the usage screen shows, read through${SP}
        <code>/api/billing</code>; plan limits come from${SP}
        <code>apps/server/src/billing/plans.ts</code>. Nothing on this page is computed locally.
      </p>
    </div>
  `;
}
